import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, emptyCosts, formatDate, formatPLN } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import ClientFields from "../components/ClientFields";
import CostTable from "../components/CostTable";
import SummaryCard from "../components/SummaryCard";
import NotesCard from "../components/NotesCard";
import { Trash2, CheckCircle2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [values, setValues] = useState({ name: "", client_name: "", client_phone: "" });

  const [plannedCosts, setPlannedCosts] = useState(emptyCosts());
  const [plannedItems, setPlannedItems] = useState([]);
  const [actualCosts, setActualCosts] = useState(emptyCosts());
  const [actualItems, setActualItems] = useState([]);
  const [deposit, setDeposit] = useState(0);
  const [paidInFull, setPaidInFull] = useState(false);
  const [notes, setNotes] = useState("");

  const [salePrice, setSalePrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const skipDirty = useRef(true);

  useEffect(() => {
    let active = true;
    api.get(`/orders/${id}`).then((r) => {
      if (!active) return;
      const o = r.data;
      setOrder(o);
      setValues({
        name: o.name,
        client_name: o.client_name || "",
        client_phone: o.client_phone || "",
      });
      setPlannedCosts({ ...emptyCosts(), ...(o.costs || {}) });
      setPlannedItems(o.custom_items || []);
      setActualCosts({ ...emptyCosts(), ...(o.actual_costs || {}) });
      setActualItems(o.actual_custom_items || []);
      setDeposit(o.deposit || 0);
      setPaidInFull(Boolean(o.paid_in_full));
      setNotes(o.notes || "");
      setSalePrice(o.sale_price || 0);
      setLoading(false);
      skipDirty.current = true;
    });
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (skipDirty.current) {
      skipDirty.current = false;
      return;
    }
    setDirty(true);
  }, [values, plannedCosts, plannedItems, actualCosts, actualItems, salePrice, deposit, paidInFull, notes]);

  const plannedTotal =
    Object.values(plannedCosts).reduce((s, v) => s + Number(v || 0), 0) +
    plannedItems.reduce((s, it) => s + Number(it?.amount || 0), 0);
  const actualTotal =
    Object.values(actualCosts).reduce((s, v) => s + Number(v || 0), 0) +
    actualItems.reduce((s, it) => s + Number(it?.amount || 0), 0);

  const isCompleted = order?.status === "completed";

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const r = await api.put(`/orders/${id}`, {
        ...values,
        costs: plannedCosts,
        custom_items: plannedItems,
        actual_costs: actualCosts,
        actual_custom_items: actualItems,
        deposit: Number(deposit || 0),
        paid_in_full: paidInFull,
        notes,
        sale_price: Number(salePrice || 0),
      });
      setOrder(r.data);
      setDirty(false);
      toast.success("Zapisano");
    } catch {
      toast.error("Błąd zapisu");
    } finally {
      setSaving(false);
    }
  }, [id, values, plannedCosts, plannedItems, actualCosts, actualItems, salePrice, deposit, paidInFull, notes]);

  const complete = async () => {
    if (dirty) await save();
    try {
      const r = await api.post(`/orders/${id}/complete`);
      setOrder(r.data);
      toast.success("Zlecenie zakończone");
      nav("/zakonczone");
    } catch {
      toast.error("Nie udało się zakończyć");
    }
  };

  const reopen = async () => {
    try {
      const r = await api.post(`/orders/${id}/reopen`);
      setOrder(r.data);
      toast.success("Zlecenie otwarte ponownie");
    } catch {
      toast.error("Błąd");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Usunięto zlecenie");
      nav(isCompleted ? "/zakonczone" : "/zlecenia");
    } catch {
      toast.error("Nie udało się usunąć");
    }
  };

  if (loading) return <div className="surface p-10 text-center" data-testid="order-loading">Wczytywanie...</div>;
  if (!order) return <div className="surface p-10 text-center">Nie znaleziono zlecenia</div>;

  const diff = actualTotal - plannedTotal;

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 flex items-center gap-2">
            <span>Zlecenie</span>
            <StatusBadge status={order.status} />
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
            {order.name}
          </h1>
          <div className="text-xs text-[var(--text-muted)] mt-2">
            Utworzono {formatDate(order.created_at)}
            {order.completed_at && <> · Zakończono {formatDate(order.completed_at)}</>}
          </div>
        </div>
      </header>

      <ClientFields values={values} onChange={setValues} disabled={isCompleted} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CostTable
          costs={plannedCosts}
          customItems={plannedItems}
          onChange={setPlannedCosts}
          onChangeCustomItems={setPlannedItems}
          readOnly={isCompleted}
          title="Wycena · planowane koszty"
          accent="#5C5C5C"
        />
        <CostTable
          costs={actualCosts}
          customItems={actualItems}
          onChange={setActualCosts}
          onChangeCustomItems={setActualItems}
          readOnly={isCompleted}
          title="Rzeczywiste poniesione koszty"
          accent="var(--primary)"
          showStandard={false}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface p-5 grid grid-cols-3 gap-4" data-testid="cost-comparison">
          <Stat label="Plan (wycena)" value={formatPLN(plannedTotal)} />
          <Stat label="Rzeczywiście" value={formatPLN(actualTotal)} accent="var(--primary)" />
          <Stat
            label="Odchylenie"
            value={`${diff >= 0 ? "+" : ""}${formatPLN(diff)}`}
            accent={diff <= 0 ? "var(--success)" : "var(--danger)"}
          />
        </div>
        <SummaryCard
          salePrice={salePrice}
          totalCosts={actualTotal}
          onChangeSalePrice={setSalePrice}
          disabled={isCompleted}
          showPayment
          deposit={deposit}
          paidInFull={paidInFull}
          onChangeDeposit={setDeposit}
          onChangePaidInFull={setPaidInFull}
        />
      </div>

      <NotesCard value={notes} onChange={setNotes} disabled={isCompleted} />

      <div className="surface p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-[var(--text-muted)]">
          {isCompleted
            ? "Zlecenie zakończone. Otwórz ponownie aby edytować."
            : dirty
              ? "Masz niezapisane zmiany"
              : "Wszystkie zmiany zapisane"}
        </div>
        <div className="flex flex-wrap gap-2">
          {!isCompleted && (
            <Button
              variant="outline"
              disabled={!dirty || saving}
              onClick={save}
              data-testid="save-order-btn"
            >
              <Save size={16} className="mr-2" />
              {saving ? "Zapisywanie..." : "Zapisz"}
            </Button>
          )}
          {!isCompleted ? (
            <Button
              onClick={complete}
              data-testid="complete-order-btn"
              className="bg-[var(--success)] hover:opacity-90 text-white"
            >
              <CheckCircle2 size={16} className="mr-2" />
              Zakończ zlecenie
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={reopen}
              data-testid="reopen-order-btn"
            >
              <RotateCcw size={16} className="mr-2" />
              Otwórz ponownie
            </Button>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white"
                data-testid="delete-order-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Usuń
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Usunąć zlecenie?</AlertDialogTitle>
                <AlertDialogDescription>
                  Tej operacji nie można cofnąć. Zlecenie zostanie trwale usunięte.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Anuluj</AlertDialogCancel>
                <AlertDialogAction
                  onClick={remove}
                  data-testid="delete-confirm"
                  className="bg-[var(--danger)] hover:bg-[var(--danger)]/90"
                >
                  Usuń
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "W toku", bg: "var(--primary)" },
    completed: { label: "Zakończone", bg: "var(--success)" },
  };
  const cfg = map[status] || map.active;
  return (
    <span
      className="px-2 py-0.5 text-[10px] font-semibold rounded text-white"
      style={{ background: cfg.bg }}
      data-testid="order-status-badge"
    >
      {cfg.label.toUpperCase()}
    </span>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide">{label}</div>
      <div
        className="heading text-xl font-semibold tabular-nums mt-1"
        style={accent ? { color: accent } : {}}
      >
        {value}
      </div>
    </div>
  );
}
